/**
 * Colunas de remuneração das grids de Colaboradores.
 *
 * O backend remove estas chaves da resposta de `/v2/cdp/summary-tables` para os
 * CNPJs restritos (`src/config/payroll-restricted-cnpjs.ts`, no
 * conducco-admin-backend — hoje, os 27 CNPJs da BRK). Aqui a grid deixa de
 * renderizar a coluna quando a chave não vem.
 *
 * A decisão é por **presença da chave**, não por valor: a query devolve
 * `NULL AS SALARY` em seis dos sete ramos do UNION para todo mundo, então
 * `SALARY === null` é o caso normal de um CNPJ sem restrição e não pode fazer a
 * coluna desaparecer. Chave ausente é o único sinal que significa "restrito".
 *
 * A lista de CNPJs vive só no backend, de propósito: duplicá-la aqui criaria
 * dois lugares para manter em sincronia e um jeito de o front discordar do
 * servidor.
 */
export const PAYROLL_COLUMN_FIELDS = ['SALARY', 'NET_SALARY'] as const

/**
 * Devolve as colunas sem as de remuneração cuja chave não existe no payload.
 *
 * Sem linhas não há o que inferir, e as colunas ficam como estão — grid vazia
 * não mostra valor nenhum de qualquer forma.
 */
export function dropRestrictedPayrollColumns<T extends { field: string }>(
  columns: T[],
  // `object`, não `Record<string, unknown>`: uma interface como `ITotal` não é
  // atribuível a um tipo com index signature, e é `ITotal[]` que chega aqui.
  rows: readonly object[] | null | undefined,
): T[] {
  if (!rows?.length) return columns

  const absent = new Set<string>(
    PAYROLL_COLUMN_FIELDS.filter((field) => !rows.some((row) => field in row)),
  )

  if (!absent.size) return columns

  return columns.filter((column) => !absent.has(column.field))
}
